import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, CreditCard, AlertTriangle, Loader2, Minus, Plus, Trash2 } from 'lucide-react';

function fmt(n) {
  return `$${Math.round(Number(n) || 0).toLocaleString('es-CL')}`;
}

/**
 * Resumen visual del pedido en el chat del asistente.
 */
export function AssistantOrderSummaryCard({
  preview,
  primaryColor = '#10b981',
  editable = false,
  onLineQtyChange,
  onRemoveLine,
}) {
  if (!preview?.lines?.length) return null;

  const { lines, subtotal, delivery, tip, total, address, payment, warnings } = preview;
  const deliveryLoading = delivery?.status === 'loading';

  return (
    <Card className="p-4 bg-card border shadow-sm" data-testid="assistant-order-summary-card">
      <p className="text-sm font-semibold mb-3">📋 Resumen del pedido</p>

      <div className="space-y-2 text-sm">
        {lines.map((line, i) => (
          <div key={`${line.cartLineId || line.name}-${i}`} className="flex justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-medium leading-snug">
                {line.qty > 1 ? `${line.qty}× ` : ''}
                {line.name}
              </p>
              {line.removed.length > 0 ? (
                <p className="text-xs text-muted-foreground">Sin {line.removed.join(', ')}</p>
              ) : null}
              {line.addOns.length > 0 ? (
                <p className="text-xs text-muted-foreground">+ {line.addOns.join(', ')}</p>
              ) : null}
              {editable && line.cartLineId ? (
                <div className="mt-2 flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Menos cantidad"
                    onClick={() => onLineQtyChange?.(line.cartLineId, -1)}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <span className="text-xs tabular-nums w-6 text-center">{line.qty}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Más cantidad"
                    onClick={() => onLineQtyChange?.(line.cartLineId, 1)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-destructive hover:text-destructive"
                    onClick={() => onRemoveLine?.(line.cartLineId)}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Quitar
                  </Button>
                </div>
              ) : null}
            </div>
            <span className="shrink-0 font-medium tabular-nums">{fmt(line.subtotal)}</span>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t space-y-1.5 text-sm">
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="tabular-nums">{fmt(subtotal)}</span>
        </div>

        {deliveryLoading ? (
          <div className="flex justify-between gap-2 text-muted-foreground">
            <span>Delivery</span>
            <span className="text-xs inline-flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Calculando…
            </span>
          </div>
        ) : delivery.status === 'ok' && typeof delivery.fee === 'number' ? (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">
              Delivery
              {delivery.zoneName ? ` (${delivery.zoneName})` : ''}
              {delivery.distanceKm != null ? ` · ${delivery.distanceKm.toFixed(1)} km` : ''}
            </span>
            <span className="tabular-nums">{fmt(delivery.fee)}</span>
          </div>
        ) : delivery.status !== 'not_applicable' ? (
          <div className="flex justify-between gap-2 text-amber-700 dark:text-amber-400">
            <span>Delivery</span>
            <span className="text-xs text-right max-w-[55%]">
              {delivery.hint || 'Por confirmar'}
            </span>
          </div>
        ) : null}

        {tip > 0 ? (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Propina</span>
            <span className="tabular-nums">{fmt(tip)}</span>
          </div>
        ) : null}

        <div className="flex justify-between gap-2 font-semibold pt-1">
          <span>Total</span>
          <span className="tabular-nums" style={{ color: primaryColor }}>
            {fmt(total)}
          </span>
        </div>
      </div>

      {address ? (
        <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden />
          <span>{address}</span>
        </div>
      ) : null}

      {payment.label ? (
        <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
          <CreditCard className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden />
          <span>
            {payment.label}
            {payment.cashAmount != null ? ` con ${fmt(payment.cashAmount)}` : ''}
            {payment.change != null ? ` (vuelto ${fmt(payment.change)})` : ''}
          </span>
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div className="mt-3 rounded-md bg-amber-500/10 border border-amber-500/25 px-3 py-2 space-y-1">
          {warnings.map((w) => (
            <p key={w} className="text-xs text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden />
              {w}
            </p>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
